/**
 * UI Utility Functions
 * Centralized functions for common UI operations
 */

import { analytics } from '../services/analytics.js';
import { emitMissionSignal } from '../missions/missionSignals.js';
import {
  TOKENS,
  surfaceFor,
  applyCardStyle,
  getTopStack,
  createActionButton,
  createIcon,
} from '../ui/designSystem.js';
import { Check, X, Video } from 'lucide';

/**
 * Create a styled overlay element
 */
export function createOverlayElement(id, styles = {}) {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement('div');
    element.id = id;
    Object.assign(element.style, {
      position: 'absolute',
      background: 'rgba(0,0,0,0.7)',
      color: '#fff',
      padding: '6px 18px',
      borderRadius: '8px',
      fontFamily: 'sans-serif',
      fontSize: '18px',
      zIndex: 101,
      pointerEvents: 'none',
      ...styles,
    });
    document.body.appendChild(element);
  }
  return element;
}

/**
 * Create a button with consistent styling
 */
export function createStyledButton(text, styles = {}) {
  const button = document.createElement('button');
  button.textContent = text;
  Object.assign(button.style, {
    fontSize: '1.1rem',
    padding: '0.5rem 1.5rem',
    borderRadius: '0.7rem',
    background: '#1976d2',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    ...styles,
  });
  return button;
}

/**
 * Check if device is mobile
 */
export function isMobile() {
  return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
}

/**
 * Check if device is in portrait mode
 */
export function isPortrait() {
  return window.innerHeight > window.innerWidth;
}

/**
 * Toggle element visibility
 */
export function toggleElementVisibility(elementId, show) {
  const element = document.getElementById(elementId);
  if (element) {
    element.style.display = show ? 'block' : 'none';
  }
}

/**
 * Create a status display element
 */
export function createStatusDisplay(id, text, position = { top: '48px', left: '16px' }) {
  return createOverlayElement(id, {
    ...position,
    minWidth: '120px',
    maxWidth: '220px',
    textContent: text,
  });
}

/**
 * Debounce function for performance optimization
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Show a media retry notification
 */
export function showMediaRetryNotification(retryCallback) {
  // Remove existing notification if any
  const existingNotification = document.getElementById('media-retry-notification');
  if (existingNotification) {
    existingNotification.remove();
  }

  const notification = document.createElement('div');
  notification.id = 'media-retry-notification';
  // Same notification-card language as the connection banner and toasts
  // (gradient surface, white text, radius, shadow). 'warn' because a media
  // failure is recoverable, not fatal.
  applyCardStyle(notification, { level: 'warn', fontSize: '14px' });
  Object.assign(notification.style, {
    // Shares the top-center stack with the connection banner so the two never
    // overlap; `order: 2` keeps this transient prompt below the persistent
    // health banner.
    order: '2',
    maxWidth: '400px',
    textAlign: 'center',
    pointerEvents: 'auto',
  });

  const message = document.createElement('div');
  Object.assign(message.style, {
    marginBottom: TOKENS.space.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: TOKENS.space.xs,
  });
  message.append(
    createIcon(Video, { size: 16 }),
    document.createTextNode('Camera/microphone access failed or timed out'),
  );

  const actions = document.createElement('div');
  Object.assign(actions.style, {
    display: 'flex',
    justifyContent: 'center',
    gap: TOKENS.space.sm,
  });
  const retryBtn = createActionButton('Try Again', { variant: 'primary' });
  const dismissBtn = createActionButton('Dismiss', { variant: 'neutral' });
  actions.append(retryBtn, dismissBtn);

  notification.append(message, actions);

  getTopStack().appendChild(notification);

  retryBtn.addEventListener('click', async () => {
    retryBtn.textContent = 'Retrying...';
    retryBtn.disabled = true;

    try {
      const success = await retryCallback();
      if (success) {
        notification.remove();
      } else {
        retryBtn.textContent = 'Try Again';
        retryBtn.disabled = false;
      }
    } catch (error) {
      console.error('Retry failed:', error);
      retryBtn.textContent = 'Try Again';
      retryBtn.disabled = false;
    }
  });

  dismissBtn.addEventListener('click', () => {
    notification.remove();
  });

  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 30000);

  return notification;
}

/**
 * Hide media retry notification
 */
export function hideMediaRetryNotification() {
  const notification = document.getElementById('media-retry-notification');
  if (notification) {
    notification.remove();
  }
}

/**
 * Show a toast notification at the bottom of the screen
 * @param {string} message - The message to display
 * @param {Object} [options] - Toast options
 * @param {string} [options.type] - 'success' | 'error' | 'info' (default: 'info')
 * @param {number} [options.duration] - Duration in ms (default: 3000)
 * @param {import('lucide').IconNode} [options.icon] - optional leading Lucide icon
 */
export function showToast(message, options = {}) {
  const { type = 'info', duration = 3000, icon } = options;

  const toast = document.createElement('div');
  // Same notification-card language as the top banners, pinned bottom-center.
  // When the meeting toolbar is mounted it owns the bottom edge (28px region
  // inset + ~64px pill), so toasts clear it instead of landing on the buttons.
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: document.getElementById('meeting-toolbar') ? '112px' : '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: surfaceFor(type),
    color: TOKENS.text.onDark,
    padding: '16px 28px',
    borderRadius: TOKENS.radius.md,
    fontFamily: TOKENS.font,
    fontSize: '16px',
    fontWeight: TOKENS.weight.medium,
    boxShadow: TOKENS.shadow.md,
    zIndex: String(TOKENS.z.toast),
    opacity: '0',
    transition: 'opacity 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: TOKENS.space.sm,
  });
  if (icon) toast.appendChild(createIcon(icon, { size: 18 }));
  const label = document.createElement('span');
  label.textContent = message;
  toast.appendChild(label);

  document.body.appendChild(toast);
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
  });

  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => {
      if (toast.parentNode) {
        document.body.removeChild(toast);
      }
    }, 300);
  }, duration);

  return toast;
}

/**
 * Download a file from a URL
 * @param {string} url - The URL to download from
 * @param {string} [filename] - Optional filename override
 * @param {{slotKey?: string, itemId?: string}} [meta] - Content-manifest
 *   attribution: when present, a content_download event fires alongside the
 *   legacy magazine_download (which keeps firing with the filename so
 *   historical dashboards stay continuous - design §6.4)
 * @returns {Promise<boolean>} - Resolves true on success, false on failure
 */
export async function downloadFile(url, filename, meta) {
  const downloadName = filename || url.split('/').pop();

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Download failed');

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    showToast(`Downloading: ${downloadName}`, { type: 'success', icon: Check });
    analytics.track('magazine_download', { filename: downloadName });
    emitMissionSignal('download', { filename: downloadName });
    if (meta && meta.itemId) {
      analytics.track('content_download', {
        slotKey: meta.slotKey || null,
        itemId: meta.itemId,
        filename: downloadName,
      });
    }
    return true;
  } catch (error) {
    console.error('Download error:', error);
    showToast(`Download failed: ${downloadName}`, { type: 'error', icon: X });
    return false;
  }
}

/**
 * Create a tooltip element
 * @param {string} text - Tooltip text
 * @returns {HTMLElement} The tooltip element
 */
export function createTooltip(text) {
  const tooltip = document.createElement('div');
  tooltip.textContent = text;
  Object.assign(tooltip.style, {
    position: 'fixed',
    left: '50%',
    top: '60%',
    transform: 'translateX(-50%)',
    background: 'rgba(0, 0, 0, 0.85)',
    color: TOKENS.text.onDark,
    padding: '8px 14px',
    borderRadius: TOKENS.radius.sm,
    fontFamily: TOKENS.font,
    fontSize: '14px',
    fontWeight: TOKENS.weight.medium,
    pointerEvents: 'none',
    zIndex: String(TOKENS.z.tooltip),
    display: 'none',
    whiteSpace: 'nowrap',
    boxShadow: TOKENS.shadow.sm,
  });
  document.body.appendChild(tooltip);
  return tooltip;
}
